import React from 'react';
import { Markdown } from '@uplift/ui-v2';
import { useAgreementTemplatePreviewQuery, useCollaboratePhysicianMatchByIdForProviderQuery, useProviderDetailsQuery, useStateQuery, useUserDisplayNameQuery } from '@uplift/queries';
import { Box, Table, TableBody, TableRow, Typography } from '@mui/material';
import { useCurrentuser } from '@uplift/contexts';
import { agreementTermTypeLabelMap, DetailsCell, DocumentLayout } from './components';
import { SignDocumentControls } from './SignDocumentControls';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { CollaborativeAgreementExpired } from '@uplift/api';


export const CollaborativeAgreementExpiredPreview: React.VFC<{
  document: CollaborativeAgreementExpired;
  onSign: (agreementId: string) => void;
}> = ({
  document,
  onSign,
}) => {
  const me = useCurrentuser();
  const { data: match } = useCollaboratePhysicianMatchByIdForProviderQuery(me.id, document.collaborativeMatchId);
  const { data: preview } = useAgreementTemplatePreviewQuery(document.agreementTemplateId);
  const { data: state } = useStateQuery(match.stateId, { suspense: true });
  const { data: physicianName } = useUserDisplayNameQuery(match.collaborativePhysicianId, { suspense: true });
  const { data: details } = useProviderDetailsQuery(match.providerId, { suspense: true });
  const expiredAt = CalendarDateService.date(document.expiredAt, DateFormats.ISO_DATE);

  return (
    <DocumentLayout
      title={preview.template.name}
      subtitle="sent by uplift"
      controls={<SignDocumentControls document={document} onSign={onSign} />}
    >
      <Typography variant="overline" color="secondary">AGREEMENT DETAILS</Typography>
      <Table size="small" sx={{ 'tr:last-of-type td': { borderBottom: 0 } }}>
        <TableBody>
          <TableRow>
            <DetailsCell label="State Covered">{state.fullName}</DetailsCell>
            <DetailsCell label="Status">
              <Typography color="error" fontWeight={500}>
                Expired at {expiredAt.format(DateFormats['MMM D, YYYY'])}
              </Typography>
            </DetailsCell>
          </TableRow>
          <TableRow>
            <DetailsCell label="Effective Date">Upon counter-signature</DetailsCell>
            <DetailsCell label="Expires">{preview.template.agreementTermValue} {agreementTermTypeLabelMap[preview.template.agreementTermType][preview.template.agreementTermValue === 1 ? 'single' : 'many']} from effective date</DetailsCell>
          </TableRow>
          <TableRow>
            <DetailsCell label="Collaborating Physician">{physicianName.defaultDisplay}</DetailsCell>
            <DetailsCell label="NPI">{details.npi ?? '-'}</DetailsCell>
          </TableRow>
        </TableBody>
      </Table>

      <Box sx={{ mt: 2 }}>
        <Typography variant="overline" color="secondary">AGREEMENT TEXT</Typography>
        <Markdown>{preview.version.content}</Markdown>
      </Box>
    </DocumentLayout>
  );
};
